import React, { useEffect, useState } from "react";
import { Card, Grid, Box, Typography, IconButton, MenuItem, Checkbox, Tooltip } from "@mui/material";
import whiteBackIcon from "../../../icons/whiteBackIcon.svg";
import { useStyles } from "../styles";
import { AppConfigSettingsViewEnum } from "../applicationsConfigSettings";
import { ApplicationOccupantDtoEmploymentStatusEnum, CustomQuestionTypeEnum } from "@inosio/payments-sdk";
import CustomQuestionFields from "./CustomQuestionFields";
import { InputField } from "../../../components/InputController";
import { applicationStyles } from "../../../resui/applications/components/CustomStyles";
import CustomDropdown from "../../../components/customDropdown";
import { useDispatch, useSelector } from "react-redux";
import { getAllPropertyGroupsConfigs, getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState } from "../../../shared/sagas/propertyGroups/propertyGroupsSlice";
import { useParams } from "react-router";
import { PmConfigType } from "../pmConfigType";
import HelpIcon from '@mui/icons-material/Help';

interface PropsType {
  setView: React.Dispatch<React.SetStateAction<AppConfigSettingsViewEnum>>;
}

export default function ApplicationsEmployee({ setView }: PropsType) {
  const classes = useStyles();
  const classes1 = applicationStyles();
  const dispatch = useDispatch();
  const { selectedPropertyGroupAssignedConfigs, shouldRefreshAllPropertyGroupConfigs} = useSelector(selectPropertyGroupDetailsState);

  const { uuid: propertyGroupIdParam } = useParams<{ uuid: string }>();
  const [pmConfig, setPmConfig] = useState<PmConfigType>({});

  // local state to simulate config update for better ux.
  const [isRequireDocsUploadChecked, setIsRequireDocsUploadChecked] = React.useState(false);

  useEffect(() => {
    setIsRequireDocsUploadChecked(!!selectedPropertyGroupAssignedConfigs.ApplicationsRequireEmploymentDocsUpload);
  }, [selectedPropertyGroupAssignedConfigs.ApplicationsRequireEmploymentDocsUpload]);

  //makes sure we get the updated configs and display them on the ui
  useEffect(() => {
    if (shouldRefreshAllPropertyGroupConfigs) dispatch(getAllPropertyGroupsConfigs());
  }, [shouldRefreshAllPropertyGroupConfigs]);

  const handleRequireDocsUploadChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsRequireDocsUploadChecked(checked);
    handleConfirmChangeRequiredDocsUpload(checked, event.target.name);
  };

  const handleConfirmChangeRequiredDocsUpload = (checked: boolean, ConfigName: string) => {
    handleConfigChange(ConfigName, checked);
  };

  //for now, this handler updates only the document upload requirement configs that's why assigned value is a boolean.
  //In the future if more config manipulation is added to this component, this method would need to updated as well.
  const handleConfigChange = (ConfigTypeKey1: string, AssignedValue1: boolean) => {
    let newPmConfig = {};
    newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
      };
    setPmConfig(newPmConfig);
  };

  useEffect(()=>{
      if (Object.keys(pmConfig).length !== 0) dispatch(getUpdatePropertyGroupConfigs({ propertyGroupUuid: propertyGroupIdParam, productTypeId: 2, updatedPmConfig: pmConfig }));
  },[pmConfig])

  const getTypography = (text: string, fontWeight: number, fontSize: string, restProps?: object) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
        ...restProps,
      }}
    >
      {text}
    </Typography>
  )

  const [employmentStatus, setEmploymentStatus] = React.useState<ApplicationOccupantDtoEmploymentStatusEnum>(ApplicationOccupantDtoEmploymentStatusEnum.Employed);

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ display: "flex", flexDirection: "column", color: "#000" }}>
        <Box className={classes.headerContainer} sx={{ backgroundColor: '#BE6531' }}>
          <IconButton
            sx={{ marginRight: "10px", marginTop: "-4px" }}
            onClick={() => setView(AppConfigSettingsViewEnum.Dashboard)}
          >
            <img src={whiteBackIcon} alt="" height="14px" />
          </IconButton>
          {getTypography('Employment & Income', 600, '24px', { color: '#fff' })}
        </Box>

        <Grid container sx={{ p: 2, pt: 2, pb: 1, position: 'relative' }}>

          <Box sx={{ width: "100%", p: 2 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} lg={4.5} xl={3.5}>
                <Typography sx={{ mb: 2 }} className={classes1.labelText}>
                  Employment
                </Typography>
                <CustomDropdown
                  isFullWidth
                  label={"EmploymentStatus"}
                  value={employmentStatus ?? ""}
                  handleChange={(e) => {
                    setEmploymentStatus(e.target.value as ApplicationOccupantDtoEmploymentStatusEnum);
                  }}
                  menuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '24px',
                      },
                    },
                  }}
                >
                  <MenuItem style={{ height: "36px" }} key={ApplicationOccupantDtoEmploymentStatusEnum.Employed} value={ApplicationOccupantDtoEmploymentStatusEnum.Employed}>
                    Employed
                  </MenuItem>
                  <MenuItem style={{ height: "36px" }} key={ApplicationOccupantDtoEmploymentStatusEnum.NotEmployed} value={ApplicationOccupantDtoEmploymentStatusEnum.NotEmployed}>
                    Not Employed
                  </MenuItem>
                  <MenuItem style={{ height: "36px" }} key={ApplicationOccupantDtoEmploymentStatusEnum.SelfEmployed} value={ApplicationOccupantDtoEmploymentStatusEnum.SelfEmployed}>
                    Self Employed
                  </MenuItem>
                </CustomDropdown>
                {employmentStatus !== ApplicationOccupantDtoEmploymentStatusEnum.NotEmployed && (
                  <React.Fragment>
                    {employmentStatus === ApplicationOccupantDtoEmploymentStatusEnum.Employed && (
                      <InputField
                        name={"EmploymentTitle"}
                        label={"Job Title"}
                        value={''}
                      />
                    )}
                    {employmentStatus === ApplicationOccupantDtoEmploymentStatusEnum.SelfEmployed && (
                      <InputField
                        name={"EmploymentIndustry"}
                        label={"Industry"}
                        value={''}
                      />
                    )}
                    <InputField
                      name={"EmploymentCompanyName"}
                      label={"Company Name"}
                      value={''}
                    />
                    {employmentStatus === ApplicationOccupantDtoEmploymentStatusEnum.Employed && (
                      <InputField
                        name={"EmploymentCompanyPhone"}
                        label={"Company Phone Number"}
                        value={''}
                      />
                    )}
                    {employmentStatus === ApplicationOccupantDtoEmploymentStatusEnum.SelfEmployed && (
                      <React.Fragment>
                        <InputField
                          name={"EmploymentDurationYears"}
                          label={"How many years"}
                          value={''}
                        />
                        <InputField
                          name={"EmploymentDurationMonths"}
                          label={"How many months"}
                          value={''}
                        />
                      </React.Fragment>
                    )}
                    <InputField
                      name={"EmploymentMonthlySalary"}
                      label={"Monthly Salary"}
                      value={''}
                    />
                  </React.Fragment>
                )}
                <InputField
                  name={"StartDate"}
                  label={"Start Date"}
                  value={''}
                />
                <Grid container>
                  <Grid item xs={6}>
                    <InputField
                      name={"EndDate"}
                      label={"End Date"}
                      value={''}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ mb: -2 }} className={classes1.bodyText}>
                      <Checkbox 
                        onChange={() => {}}
                        inputProps={{ 'aria-label': 'controlled' }} 
                        sx={{ ml: 1 }}
                      />
                      Currently employed
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6} lg={4.5} xl={3.5}>
                {employmentStatus === ApplicationOccupantDtoEmploymentStatusEnum.Employed && (
                  <React.Fragment>
                    <Typography sx={{ mb: 2 }} className={classes1.labelText}>
                      Verification:
                    </Typography>
                    <InputField
                      name={"EmploymentCompanyContactName"}
                      label={"Supervisor/HR Contact Name"}
                      value={''}
                    />
                    <InputField
                      name={"EmploymentCompanyContactPhone"}
                      label={"Phone Number"}
                      value={''}
                    />
                    <InputField
                      name={"EmploymentCompanyContactEmail"}
                      label={"Email Address"}
                      value={''}
                    />                
                  </React.Fragment>
                )}

                <Typography sx={{ my: 2 }} className={classes1.labelText}>
                  Upload proof of income:
                </Typography>
                <Typography sx={{ ml: 1, mb: 2 }} className={classes1.bodyText}>
                  {employmentStatus === ApplicationOccupantDtoEmploymentStatusEnum.Employed ? (
                    'Please provide proof of income in the form of 2~3 paystubs or similar financial evidence.'
                  ) : (
                    'Upload proof of income. e.g W2, paystubs, annual tax forms, bank account statments showing regular deposits, etc.'
                  )}
                </Typography>
                <Typography sx={{ mb: -2 }} className={classes1.bodyTextSmall}>
                  {/* PMs can configure which sections require a document upload from the applicant */}
                  <Checkbox
                    name={'ApplicationsRequireEmploymentDocsUpload'} //This should be a config key
                    inputProps={{ 'aria-label': 'controlled' }}
                    checked={isRequireDocsUploadChecked}
                    onChange={handleRequireDocsUploadChange}
                    sx={{ ml: 1 }}
                  />
                  Document upload required
                  <Tooltip title="Select this checkbox if you want to require applicants to upload their employment documents" placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <CustomQuestionFields CustomQuestionType={CustomQuestionTypeEnum.EmployeeV1} />
        </Grid>
      </Card>
    </Grid>
  );
}
